import { useEffect, useState } from "react";
import Icon from "../../../components/base/Icon";
import TextBox from "../../../components/form/TextBox";
import { PaymentTypes } from "../../../core/Enums";
import { getEPayRate, getEPayTypes, getPaymentTypeDetails } from "../../../core/Helper";
import { PaymentTypeRate } from "../../../models/AccountPreferences";

type Props = {
    // Field names are `${namePrefix}_${type}`; the shown types go in `${namePrefix}Types`.
    namePrefix: string
    rates?: PaymentTypeRate[]
    placeholder?: (type: PaymentTypes) => string | undefined
}

/**
 * Fee rates for e-payment types, shown only for the types the user added. Removing a row drops
 * that type's rate (0% on the account defaults, back to the default on a technician). The list of
 * types comes from the enum, so a new e-payment type can be added here without touching this.
 */
export default function EPayRateFields(props: Props) {

    const [types, typesSet] = useState<PaymentTypes[]>([]);

    useEffect(() => {
        typesSet((props.rates ?? []).map(r => r.paymentType).filter(t => getEPayTypes().includes(t)));
    }, [props.rates])

    const available = getEPayTypes().filter(t => !types.includes(t));

    return (
        <>
            {/* Tells the server which rows are on the form, so a removed row is cleared instead of kept. */}
            <input type="hidden" name={`${props.namePrefix}Types`} value={types.join(",")} />

            {
                types.map(t =>
                    <TextBox key={`${props.namePrefix}_${t}`} name={`${props.namePrefix}_${t}`}
                        label={`${getPaymentTypeDetails(t).name} Rate`}
                        placeholder={props.placeholder ? props.placeholder(t) : undefined}
                        percent icon="percent" value={getEPayRate(props.rates, t)?.toString()}
                        actionIcon="circle-minus" actionIconClass="text-danger" actionTitle="Remove"
                        actionClick={() => typesSet(types.filter(f => f != t))} />
                )
            }

            {
                available.length > 0 &&
                <div className="row mb-5">
                    <div className="col col-12 col-md-2"></div>
                    <div className="col col-12 col-md-6">
                        <div className="text-muted fs-8 mb-2">Add a rate for an e-payment type:</div>
                        <div className="d-flex flex-wrap gap-2">
                            {
                                available.map(t =>
                                    <button key={`add-${props.namePrefix}-${t}`} type="button" className="btn btn-sm btn-light-primary py-2 px-3"
                                        onClick={() => typesSet([...types, t].sort((a, b) => a - b))}>
                                        <Icon icon="plus" className="me-1" />
                                        {getPaymentTypeDetails(t).name}
                                    </button>
                                )
                            }
                        </div>
                    </div>
                </div>
            }
        </>
    )
}
